/**
 * components/procurement/YoyEscalationDialog.jsx — create a scenario that steps
 * rates up by a different % each year (year-on-year escalation schedule).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { TrendingUp, Loader2 } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { createScenario } from '../../api/budgets';

export function YoyEscalationDialog({ open, onOpenChange, accountId, budgetId, horizonMonths, onSaved }) {
  const years = Math.max(1, Math.round((horizonMonths || 12) / 12));
  const steps = Math.max(0, years - 1); // year1 is the base; steps apply from year 2
  const [name, setName] = useState('');
  const [supply, setSupply] = useState([]);
  const [delivery, setDelivery] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName('YoY escalation');
      setSupply(Array.from({ length: steps }, () => '4'));
      setDelivery(Array.from({ length: steps }, () => '3'));
    }
  }, [open, steps]);

  const setAt = (setter, i, v) => setter((arr) => arr.map((x, idx) => (idx === i ? v : x)));

  const submit = async () => {
    if (steps === 0) { toast.error('This budget is only one year — no YoY steps to apply.'); return; }
    setSaving(true);
    try {
      await createScenario({
        accountId, id: budgetId,
        payload: {
          name: (name || 'YoY escalation').trim(),
          assumptions: {
            escalation: {}, rate_overrides: [], tax_pct: null, added_charges: [],
            escalation_schedule: {
              supply: supply.map((v) => parseFloat(v) || 0),
              delivery: delivery.map((v) => parseFloat(v) || 0),
            },
            source: 'yoy',
          },
        },
      });
      toast.success('YoY scenario created');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error('Could not create scenario', { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="yoy-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-indigo-600" /> Apply year-on-year % increase
          </DialogTitle>
          <DialogDescription>
            Step the supply &amp; delivery rate up by a different % each year (compounding). Usage
            still follows last year's seasonality.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="yoy-name">Scenario name</Label>
            <Input id="yoy-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="yoy-name-input" />
          </div>

          {steps === 0 ? (
            <p className="text-sm text-muted-foreground">This budget covers a single year, so there are no year-on-year steps to set.</p>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center text-xs font-medium text-muted-foreground">
                <span>Period</span><span className="w-24 text-center">Supply %</span><span className="w-24 text-center">Delivery %</span>
              </div>
              {Array.from({ length: steps }).map((_, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
                  <span className="text-sm">Year {i + 1} → {i + 2}</span>
                  <Input type="number" step="0.5" className="w-24" value={supply[i] ?? ''} onChange={(e) => setAt(setSupply, i, e.target.value)} data-testid={`yoy-supply-${i}`} />
                  <Input type="number" step="0.5" className="w-24" value={delivery[i] ?? ''} onChange={(e) => setAt(setDelivery, i, e.target.value)} data-testid={`yoy-delivery-${i}`} />
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || steps === 0} data-testid="yoy-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating…</> : 'Create scenario'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
